import { PersonIcon } from '@radix-ui/react-icons'
import { useEffect, useRef, useState } from 'react'

export type AddedPerformer = { id: string; name: string }

export type AddPerformerResult =
	| { performer: AddedPerformer; existed: boolean }
	| { error: string }

/**
 * A modal for adding a performer without leaving the concert form: a name and
 * an optional one-liner, saved straight away. A native <dialog>, so focus
 * stays inside it, Esc closes it and focus returns to the button that opened
 * it. Render it outside the concert's <form>: it has a form of its own.
 */
export function AddPerformerDialog({
	open,
	onClose,
	onAdded,
}: {
	open: boolean
	onClose: () => void
	onAdded: (performer: AddedPerformer, existed: boolean) => void
}) {
	const dialogRef = useRef<HTMLDialogElement>(null)
	const [pending, setPending] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		const dialog = dialogRef.current
		if (!dialog) return
		if (open && !dialog.open) {
			setError(null)
			dialog.showModal()
		}
		if (!open && dialog.open) dialog.close()
	}, [open])

	async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault()
		if (pending) return
		const form = e.currentTarget
		const formData = new FormData(form)
		setPending(true)
		setError(null)
		try {
			const response = await fetch('/resources/performers', {
				method: 'POST',
				headers: { 'content-type': 'application/json' },
				body: JSON.stringify({
					name: formData.get('name'),
					shortBio: formData.get('shortBio'),
				}),
			})
			const result = (await response.json()) as AddPerformerResult
			if ('error' in result) throw new Error(result.error)
			form.reset()
			onAdded(result.performer, result.existed)
			onClose()
		} catch (err) {
			setError(
				err instanceof Error && err.message
					? err.message
					: 'Kunne ikkje legge til artisten. Prøv igjen.',
			)
		} finally {
			setPending(false)
		}
	}

	return (
		<dialog
			ref={dialogRef}
			className="modal modal-bottom sm:modal-middle"
			aria-labelledby="add-performer-title"
			onClose={onClose}
		>
			<div className="modal-box bg-sheet max-w-md p-6 sm:rounded-[1.25rem] sm:p-7">
				<form onSubmit={(e) => void handleSubmit(e)} className="grid gap-5">
					<header className="flex items-center gap-3">
						<span
							className="bg-navy-2 text-lamp grid size-10 shrink-0 place-items-center rounded-xl [&>svg]:size-[1.15rem]"
							aria-hidden="true"
						>
							<PersonIcon />
						</span>
						<div>
							<h2
								id="add-performer-title"
								className="text-xl leading-tight font-semibold"
							>
								Ny artist
							</h2>
							<p className="text-muted-ink text-sm">
								Blir lagd til og vald for arrangementet med ein gong.
							</p>
						</div>
					</header>

					<div className="grid gap-1.5">
						<label
							htmlFor="new-performer-name"
							className="text-sm font-semibold"
						>
							Namn
						</label>
						<input
							type="text"
							id="new-performer-name"
							name="name"
							required
							autoComplete="off"
							className="input w-full"
						/>
					</div>

					<div className="grid gap-1.5">
						<label
							htmlFor="new-performer-shortBio"
							className="text-sm font-semibold"
						>
							Kort presentasjon{' '}
							<span className="text-muted-ink font-normal">(valfri)</span>
						</label>
						<textarea
							id="new-performer-shortBio"
							name="shortBio"
							rows={2}
							maxLength={200}
							className="textarea field-sizing-content min-h-20 w-full"
							aria-describedby="new-performer-shortBio-hint"
						/>
						<p
							id="new-performer-shortBio-hint"
							className="text-muted-ink text-xs"
						>
							Éi linje til artistoversikta. Bilete og ein lengre presentasjon
							legg du til på artistsida seinare.
						</p>
					</div>

					<p aria-live="polite" className="text-error text-sm empty:hidden">
						{error}
					</p>

					<div className="flex flex-wrap justify-end gap-2">
						<button type="button" className="btn btn-ghost" onClick={onClose}>
							Avbryt
						</button>
						<button
							type="submit"
							className="btn btn-primary"
							disabled={pending}
						>
							{pending ? 'Legg til…' : 'Legg til artist'}
						</button>
					</div>
				</form>
			</div>
			<form method="dialog" className="modal-backdrop">
				<button type="submit" tabIndex={-1}>
					Lukk
				</button>
			</form>
		</dialog>
	)
}
